import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { api, API } from "@/lib/api";
import { EVENT_TYPES } from "@/data/site";
import PaymentPanel from "@/components/PaymentPanel";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

const PKG_ESTIMATE = { "K.O. ESSENTIAL": 40, "K.O. SIGNATURE": 60, "K.O. PREMIUM": 75, "K.O. CORPORATE BUFFET": 48 };
const ADDITIONAL_SERVICES_PRICED = [{ name: "Live Chef Station", price: 300 }, { name: "Floral & Décor", price: 300 }];
const SVC_PRICE = { "Live Chef Station": 300, "Floral & Décor": 300 };

function QuoteBreakdown({ b, services }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-6 md:p-8 space-y-3" data-testid="quote-breakdown">
      <div className="flex justify-between text-sm gap-4">
        <span className="text-white/60">Comida y Menú <span className="text-white/30 hidden sm:inline">/ Food & Menu</span></span>
        <span className="text-white shrink-0" data-testid="q-food">${b.food.toFixed(2)}</span>
      </div>
      <div className="flex justify-between text-sm gap-4">
        <span className="text-white/60">Cargo por Servicio de Catering</span>
        <span className="text-white shrink-0" data-testid="q-service">${b.service.toFixed(2)}</span>
      </div>
      {services.map((s) => (
        <div key={s} className="flex justify-between text-xs pl-4 gap-4"><span className="text-white/40">+ {s}</span><span className="text-white/60 shrink-0">${(SVC_PRICE[s] || 0).toFixed(2)}</span></div>
      ))}
      <div className="flex justify-between text-sm border-t border-white/10 pt-3 gap-4"><span className="text-white/60">Subtotal</span><span className="text-white shrink-0" data-testid="q-subtotal">${b.subtotal.toFixed(2)}</span></div>
      <div className="flex justify-between text-sm gap-4"><span className="text-white/60">Sales Tax ({b.rate}%)</span><span className="text-white shrink-0" data-testid="q-tax">${b.tax.toFixed(2)}</span></div>
      <div className="flex justify-between items-center border-t border-gold/30 pt-4 mt-1 gap-4"><span className="text-white uppercase tracking-widest text-sm">Total</span><span className="text-3xl font-serif text-gold shrink-0" data-testid="q-estimate">${b.total.toFixed(2)}</span></div>
    </div>
  );
}

export default function Quote() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [done, setDone] = useState(false);
  const [payQuote, setPayQuote] = useState(null);
  const [sending, setSending] = useState(false);
  const [build, setBuild] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [f, setF] = useState({
    full_name: "", phone: "", email: "", event_date: "", event_time: "", venue_address: "",
    city: "", guests: 25, event_type: "", budget: "", selected_package: "", special_requests: "",
    additional_services: [],
  });

  useEffect(() => {
    const b = localStorage.getItem("ko_build");
    if (b) {
      const d = JSON.parse(b);
      setBuild(d);
      setF((p) => ({ ...p, event_type: d.eventType || p.event_type, guests: d.guests || p.guests, special_requests: d.requests || "" }));
    }
  }, []);

  useEffect(() => {
    const lq = localStorage.getItem("ko_lastquote");
    if (lq && user && user.email) { setPayQuote(JSON.parse(lq)); setDone(true); }
  }, [user]);

  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleSvc = (s) => setF((p) => ({ ...p, additional_services: p.additional_services.includes(s) ? p.additional_services.filter((x) => x !== s) : [...p.additional_services, s] }));

  const foodSubtotal = () => (build?.total ? build.total : (PKG_ESTIMATE[f.selected_package] || 0) * (Number(f.guests) || 0));
  const additionalTotal = () => f.additional_services.reduce((a, s) => a + (SVC_PRICE[s] || 0), 0);
  const breakdown = () => {
    const food = foodSubtotal();
    const service = pricing?.service_fee_default ?? 0;
    const add = additionalTotal();
    const c = pricing || { tax_food: true, tax_service: false, tax_additional: false, sales_tax_rate: 0 };
    let taxable = 0;
    if (c.tax_food) taxable += food;
    if (c.tax_service) taxable += service;
    if (c.tax_additional) taxable += add;
    const subtotal = food + service + add;
    const tax = taxable * (c.sales_tax_rate || 0) / 100;
    return { food, service, add, taxable, subtotal, tax, total: subtotal + tax, rate: c.sales_tax_rate || 0 };
  };
  const estimate = () => breakdown().total;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const b = breakdown();
      const { data } = await api.post("/quotes", {
        ...f, guests: Number(f.guests),
        food_subtotal: b.food, additional_total: b.add, estimated_total: b.total,
        menu_selections: build?.menu || {},
      });
      localStorage.removeItem("ko_build");
      const pq = { quote_id: data.id, pdf_token: data.pdf_token, estimated_total: b.total, event_date: f.event_date, event_type: f.event_type };
      localStorage.setItem("ko_lastquote", JSON.stringify(pq));
      setPayQuote(pq);
      setDone(true);
      toast.success("Quote request sent!");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSending(false); }
  };

  if (done) return (
    <div data-testid="quote-success">
      <PageHero overline="Request Received" title="Thank You!" subtitle="Your quote request has been received — reserve your date below."
        image="https://images.unsplash.com/photo-1561593367-66c79c2294e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" />
      <section className="py-16 md:py-20">
        <div className="container-lux max-w-2xl space-y-8">
          <div className="text-center">
            <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-4" />
            <p className="text-white/60 max-w-md mx-auto">Our team will reach out within 24 hours. To secure your date now, choose a payment option below.</p>
          </div>
          <PaymentPanel quote={payQuote || { estimated_total: estimate(), event_type: f.event_type, event_date: f.event_date }} />
          {(payQuote?.quote_id) && (
            <div className="text-center">
              <a href={`${API}/quotes/${payQuote.quote_id}/pdf?t=${payQuote.pdf_token || ""}`} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="download-pdf">Descargar Cotización (PDF)</a>
            </div>
          )}
          <div className="text-center">
            <button onClick={() => { localStorage.removeItem("ko_lastquote"); setDone(false); setPayQuote(null); }} className="text-gold text-sm uppercase tracking-widest" data-testid="quote-new">Request Another Quote</button>
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <div data-testid="quote-page">
      <Seo
        title="Request a Catering Quote | K.O Chef & Catering — Southwest Florida"
        description="Request a free, personalized catering quote from K.O CHEF & CATERING. Weddings, corporate events and private dining in Naples, Bonita Springs & Fort Myers, FL. Estimate calculated instantly."
        path="/request-a-quote"
      />
      <PageHero overline="Request a Quote" title="Let's Plan Your Event"
        subtitle="Share the details and receive a personalized estimate within 24 hours."
        image="https://images.unsplash.com/photo-1561593367-66c79c2294e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" />
      <section className="py-16 md:py-24">
        <div className="container-lux max-w-4xl">
          {build && <div className="border border-gold/40 bg-gold/[0.05] p-5 mb-10 text-sm text-white/70" data-testid="quote-build-notice">Your custom menu (est. <span className="text-gold">${build.total.toFixed(0)}</span>) has been attached to this request.</div>}
          <Reveal>
            <form onSubmit={submit} className="space-y-10" data-testid="quote-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
                <input required value={f.full_name} onChange={set("full_name")} placeholder="Full Name *" className="lux-input" data-testid="q-name" />
                <input required type="email" value={f.email} onChange={set("email")} placeholder="Email *" className="lux-input" data-testid="q-email" />
                <input value={f.phone} onChange={set("phone")} placeholder="Phone" className="lux-input" data-testid="q-phone" />
                <input value={f.city} onChange={set("city")} placeholder="City" className="lux-input" data-testid="q-city" />
                <div><label className="text-xs text-white/40 uppercase tracking-widest">Event Date</label><input type="date" value={f.event_date} onChange={set("event_date")} className="lux-input" data-testid="q-date" /></div>
                <div><label className="text-xs text-white/40 uppercase tracking-widest">Event Time</label><input type="time" value={f.event_time} onChange={set("event_time")} className="lux-input" data-testid="q-time" /></div>
                <input value={f.venue_address} onChange={set("venue_address")} placeholder="Venue Address" className="lux-input md:col-span-2" data-testid="q-venue" />
                <div><label className="text-xs text-white/40 uppercase tracking-widest">Guests</label><input type="number" min="1" value={f.guests} onChange={set("guests")} className="lux-input" data-testid="q-guests" /></div>
                <div><label className="text-xs text-white/40 uppercase tracking-widest">Event Type</label>
                  <select value={f.event_type} onChange={set("event_type")} className="lux-input" data-testid="q-event-type">
                    <option value="" className="bg-[#111]">Select…</option>
                    {EVENT_TYPES.map((t) => <option key={t} value={t} className="bg-[#111]">{t}</option>)}
                  </select>
                </div>
                <input value={f.budget} onChange={set("budget")} placeholder="Budget (optional)" className="lux-input" data-testid="q-budget" />
                <div><label className="text-xs text-white/40 uppercase tracking-widest">Package</label>
                  <select value={f.selected_package} onChange={set("selected_package")} className="lux-input" data-testid="q-package">
                    <option value="" className="bg-[#111]">None / Custom</option>
                    {Object.keys(PKG_ESTIMATE).map((p) => <option key={p} value={p} className="bg-[#111]">{p}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs text-white/40 uppercase tracking-widest mb-4 block">Additional Services</label>
                <div className="flex flex-wrap gap-3">
                  {ADDITIONAL_SERVICES_PRICED.map((s) => (
                    <button type="button" key={s.name} onClick={() => toggleSvc(s.name)} data-testid={`q-svc-${s.name.replace(/\s+/g,"-")}`}
                      className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${f.additional_services.includes(s.name) ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-white/60 hover:border-gold/50"}`}>{s.name} · ${s.price}</button>
                  ))}
                </div>
              </div>

              <textarea value={f.special_requests} onChange={set("special_requests")} rows={4} placeholder="Special requests, dietary needs, vision…" className="lux-input resize-none" data-testid="q-requests" />

              <QuoteBreakdown b={breakdown()} services={f.additional_services} />

              <button disabled={sending} className="btn-gold w-full" data-testid="q-submit">{sending ? "Sending…" : "Submit Quote Request"}</button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
